import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const Pagination = ({
  current = 1,
  total = 0,
  pageSize = 10,
  onChange = () => {},
  onPageSizeChange = () => {},
  showSizeChanger = true,
  showQuickJumper = false,
  showTotal = true,
  className = '',
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startRecord = (current - 1) * pageSize + 1;
  const endRecord = Math.min(current * pageSize, total);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(totalPages - 1, current + delta);

    // Always show first page
    if (totalPages > 0) pages.push(1);

    // Add dots if there's a gap
    if (rangeStart > 2) pages.push('...');

    // Add pages around current page
    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }

    // Add dots if there's a gap
    if (rangeEnd < totalPages - 1) pages.push('...');

    // Always show last page
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== current) {
      onChange(page);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    const newCurrent = Math.min(current, Math.ceil(total / newPageSize));
    onPageSizeChange(newPageSize, newCurrent);
  };

  if (total === 0) return null;

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {/* Total records info */}
      {showTotal && (
        <div className="text-sm text-gray-700">
          Showing {startRecord} to {endRecord} of {total} entries
        </div>
      )}

      <div className="flex items-center space-x-4">
        {/* Page size selector */}
        {showSizeChanger && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>
        )}

        {/* Pagination controls */}
        <div className="flex items-center space-x-1">
          {/* Previous button */}
          <button
            onClick={() => handlePageChange(current - 1)}
            disabled={current === 1}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((pageNumber, index) => (
            <React.Fragment key={index}>
              {pageNumber === '...' ? (
                <span className="px-3 py-2 text-gray-500">...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-2 border text-sm font-medium rounded-md ${
                    current === pageNumber
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNumber}
                </button>
              )}
            </React.Fragment>
          ))}

          {/* Next button */}
          <button
            onClick={() => handlePageChange(current + 1)}
            disabled={current === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Quick jumper */}
        {showQuickJumper && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Go to</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page);
                    e.target.value = '';
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination; 